import type {
  ClientSiblingFormValues,
  FamilyAdditionalInfoConfig,
  FamilyMemberFormValues,
} from './types';

export const FAMILY_ADDITIONAL_INFO_TYPE = {
  MATERNAL_MEDICAL_HISTORY: 'MATERNAL_MEDICAL_HISTORY',
  PATERNAL_MEDICAL_HISTORY: 'PATERNAL_MEDICAL_HISTORY',
  COMMUNITY_SUPPORT: 'COMMUNITY_SUPPORT',
} as const;

export const FAMILY_MEMBER_TEXT_MIN_LENGTH = 4;

export const SIBLING_DIAGNOSIS_MIN_LENGTH = 4;

// Role that has no caregiver responsibilities, so `caregiver_type` is cleared and not required.
export const MEMBER_ROLE_PARENT_NOT_CAREGIVER = 'Parent not a caregiver';

export const MEMBER_ROLE_PARENT_AND_CAREGIVER = 'Parent and a caregiver';

export const MEMBER_ROLE_CAREGIVER = 'Caregiver';

export const EMPTY_FAMILY_MEMBER_VALUES: FamilyMemberFormValues = {
  full_name: '',
  member_role: '',
  caregiver_type: null,
  relationship_to_client: '',
  custody_type: '',
  marital_status: '',
  job_title: '',
  email: '',
  phone: '',
  street_address_line_one: '',
  street_address_line_two: '',
  street_address_city: '',
  street_address_state: '',
  street_address_zip: '',
};

export const EMPTY_CLIENT_SIBLING_VALUES: ClientSiblingFormValues = {
  sibling_relationship: '',
  sibling_age: '',
  sibling_diagnosis: '',
};

export const SIBLING_RELATIONSHIP_OPTIONS = [
  { value: 'Brother', label: 'Brother' },
  { value: 'Sister', label: 'Sister' },
  { value: 'Twins', label: 'Twins' },
  { value: 'Half-brother', label: 'Half brother' },
  { value: 'Half-sister', label: 'Half sister' },
  { value: 'Step-siblings', label: 'Step siblings' },
  { value: 'Adoptive-siblings', label: 'Adoptive siblings' },
] as const;

export const MEMBER_ROLE_OPTIONS = [
  { value: MEMBER_ROLE_PARENT_AND_CAREGIVER, label: MEMBER_ROLE_PARENT_AND_CAREGIVER },
  { value: MEMBER_ROLE_PARENT_NOT_CAREGIVER, label: MEMBER_ROLE_PARENT_NOT_CAREGIVER },
  { value: MEMBER_ROLE_CAREGIVER, label: MEMBER_ROLE_CAREGIVER },
] as const;

export const CAREGIVER_TYPE_OPTIONS = [
  { value: 'Primary caregiver', label: 'Primary caregiver' },
  { value: 'Secondary caregiver', label: 'Secondary caregiver' },
] as const;

export const FAMILY_RELATIONSHIP_TO_CLIENT_OPTIONS = [
  { value: 'Parent (Mother / Father)', label: 'Parent (Mother / Father)' },
  { value: 'Stepparent', label: 'Stepparent' },
  { value: 'Grandparent', label: 'Grandparent' },
  { value: 'Aunt / Uncle', label: 'Aunt / Uncle' },
  { value: 'Older sibling', label: 'Older sibling' },
  { value: 'Cousin', label: 'Cousin' },
  { value: 'Legal guardian (court-appointed)', label: 'Legal guardian (court-appointed)' },
  { value: 'Adoptive parent', label: 'Adoptive parent' },
  { value: 'Foster parent', label: 'Foster parent' },
  { value: 'Kinship caregiver (relative/friend)', label: 'Kinship caregiver (relative/friend)' },
] as const;

export const CUSTODY_TYPE_OPTIONS = [
  { value: 'Joint custody', label: 'Joint custody' },
  { value: 'Full custody', label: 'Full custody' },
  { value: 'No custody', label: 'No custody' },
] as const;

export const MARITAL_STATUS_OPTIONS = [
  { value: 'Divorced', label: 'Divorced' },
  { value: 'Married', label: 'Married' },
  { value: 'Single', label: 'Single' },
  { value: 'Separated', label: 'Separated' },
  { value: 'Widowed', label: 'Widowed' },
  { value: 'Domestic Partner / Civil Union', label: 'Domestic Partner / Civil Union' },
] as const;

// Shared by the maternal and paternal medical-history dropdowns.
export const FAMILY_MEDICAL_HISTORY_OPTIONS = [
  { value: 'ASD', label: 'ASD' },
  {
    value: 'Other developmental disorders (e.g., intellectual disabilities, language disorders)',
    label: 'Other developmental disorders (e.g., intellectual disabilities, language disorders)',
  },
  {
    value: 'Mood disorders (e.g., depression, bipolar disorder, anxiety disorders)',
    label: 'Mood disorders (e.g., depression, bipolar disorder, anxiety disorders)',
  },
  { value: 'ADHD', label: 'ADHD' },
  { value: 'Schizophrenia', label: 'Schizophrenia' },
  { value: 'Alcohol or drug abuse', label: 'Alcohol or drug abuse' },
  { value: 'Learning disabilities', label: 'Learning disabilities' },
  { value: 'Anxiety, panic concerns', label: 'Anxiety, panic concerns' },
  {
    value: 'Genetic syndromes associated with ASD (e.g., Fragile X syndrome, Rett syndrome)',
    label: 'Genetic syndromes associated with ASD (e.g., Fragile X syndrome, Rett syndrome)',
  },
  { value: 'Other', label: 'Other' },
  { value: 'None', label: 'None' },
] as const;

export const COMMUNITY_SUPPORT_OPTIONS = [
  {
    value: 'Church (or other religion organization)',
    label: 'Church (or other religion organization)',
  },
  { value: 'Community', label: 'Community' },
  { value: 'Extended family', label: 'Extended family' },
  { value: 'Friends', label: 'Friends' },
  { value: 'Other', label: 'Other' },
  { value: 'None', label: 'None' },
] as const;

export const FAMILY_ADDITIONAL_INFO_CONFIGS: ReadonlyArray<FamilyAdditionalInfoConfig> = [
  {
    type: FAMILY_ADDITIONAL_INFO_TYPE.MATERNAL_MEDICAL_HISTORY,
    entriesKey: 'maternal_medical_history',
    heading: 'Family medical history maternal side',
    label: 'Maternal medical history',
    addButtonLabel: 'Add condition',
    placeholder: 'Select a condition',
    options: FAMILY_MEDICAL_HISTORY_OPTIONS,
  },
  {
    type: FAMILY_ADDITIONAL_INFO_TYPE.PATERNAL_MEDICAL_HISTORY,
    entriesKey: 'paternal_medical_history',
    heading: 'Family medical history paternal side',
    label: 'Paternal medical history',
    addButtonLabel: 'Add condition',
    placeholder: 'Select a condition',
    options: FAMILY_MEDICAL_HISTORY_OPTIONS,
  },
  {
    type: FAMILY_ADDITIONAL_INFO_TYPE.COMMUNITY_SUPPORT,
    entriesKey: 'community_support',
    heading: 'Family or community support',
    label: 'Community support',
    addButtonLabel: 'Add support',
    placeholder: 'Select a support',
    options: COMMUNITY_SUPPORT_OPTIONS,
  },
];
